import { BLANK_SLATE_DOCUMENT } from '../core/constants';
import {
  useCallback, useEffect, useRef, useState, MutableRefObject,
} from 'react';
import { PageSlugs } from './types';
import { attendantTitles } from '../wedding-party/constants';
import {
  AccommodationDetails, AnchorDetails,
  AttendantDetails, CashRegistryDetails, DividerDetails, GalleryDetails, ImageAndTextDetails,
  LocationDetails, RegistryDetails,
  RichTextType,
  SectionDetailsType, TestValues,
  SectionTypes, Site, TextDetails,
} from '../resources/modelTypes';
import isEqual from 'lodash/isEqual';

export const isUsingNewComponents = (site: Site): boolean => site.siteDetails?.sectionEditorExperience === TestValues.Experiment;

export const getDefaultSectionDetailsByType = (sectionType: SectionTypes): SectionDetailsType => {
  switch (sectionType) {
    case SectionTypes.Text:
      return {
        content: BLANK_SLATE_DOCUMENT,
        title: '',
      } as TextDetails;
    case SectionTypes.Gallery:
      return {
        content: BLANK_SLATE_DOCUMENT,
        galleryMode: 'inline',
        title: '',
      } as GalleryDetails;
    case SectionTypes.ImageAndText:
      return {
        content: BLANK_SLATE_DOCUMENT,
        displayMode: 'bottom',
        title: '',
      } as ImageAndTextDetails;
    case SectionTypes.Anchor:
      return {
        anchorText: '',
        websiteUrl: '',
      } as AnchorDetails;
    case SectionTypes.Registry:
      return {
        buttonText: null,
        content: BLANK_SLATE_DOCUMENT,
        title: '',
        websiteUrl: '',
      } as RegistryDetails;
    case SectionTypes.CashRegistry:
      return {} as CashRegistryDetails;
    case SectionTypes.Location:
      return {
        address1: '',
        address2: '',
        city: '',
        content: BLANK_SLATE_DOCUMENT,
        country: '',
        hasPhysicalAddress: true,
        state: '',
        title: '',
        venue: '',
        zipCode: '',
      } as LocationDetails;
    case SectionTypes.Divider:
      return {} as DividerDetails;
    case SectionTypes.Attendant:
      return {
        content: BLANK_SLATE_DOCUMENT,
        name: '',
        photo: '',
        position: '',
        title: attendantTitles[0].value,
      } as AttendantDetails;
    case SectionTypes.Accommodation:
      return {
        anchorText: null,
        content: BLANK_SLATE_DOCUMENT,
        title: '',
        websiteUrl: '',
      } as AccommodationDetails;
    // ADD NEW SECTION TYPES HERE
    default:
      return {};
  }
};

export const SECTION_DISPLAY_NAME: Record<string, string> = {
  [SectionTypes.Attendant]: 'person',
  [SectionTypes.Accommodation]: 'travel',
};

export const useWebsiteWidth = (): {
  ref: MutableRefObject<HTMLDivElement | null>;
  width: number | null;
} => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [
    width,
    setWidth,
  ] = useState<number | null>(null);

  useEffect(() => {
    const node = ref.current;

    if (!node) {
      return;
    }

    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      const entry = entries[0];

      if (entry) {
        setWidth(entry.contentRect.width);
      }
    });

    observer.observe(node);

    // Set initial width immediately
    setWidth(node.getBoundingClientRect().width);

    return () => observer.disconnect();
  }, [
    ref,
  ]);

  return {
    ref,
    width,
  };
};

export const useStickyState = (): {
  isStuck: boolean;
  sentinelRef: (node: HTMLDivElement | null) => void;
} => {
  const [
    isStuck,
    setIsStuck,
  ] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Ref callback pattern so we know when the node mounts/unmounts
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    // Cleanup previous observer if it exists
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (node) {
      const observer = new IntersectionObserver(
        ([
          entry,
        ]) => setIsStuck(!entry.isIntersecting),
        {
          threshold: 0,
        }
      );

      observer.observe(node);
      observerRef.current = observer;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
  }, []);

  return {
    isStuck,
    sentinelRef,
  };
};

export const slugToPrimarySectionType = {
  [PageSlugs.Accommodations]: SectionTypes.Accommodation,
  [PageSlugs.Photos]: SectionTypes.Gallery,
  [PageSlugs.WeddingParty]: SectionTypes.Attendant,
  [PageSlugs.Events]: SectionTypes.Event__FAKE,
  [PageSlugs.Registry]: SectionTypes.Registry,
};

export const allSlugs: Set<string> = new Set(Object.values(PageSlugs));

export const isEmptyRichText = (content: RichTextType | null): boolean => !content || isEqual(content, BLANK_SLATE_DOCUMENT);
